import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import {
  Plus, Search, Edit2, Trash2, X, Filter, Package,
  Hash, Calendar, DollarSign, Loader, AlertCircle, MapPin, Tag, ShoppingCart
} from 'lucide-react';
import { authHeaders } from '../utils/api';
import AlertasVeiculosPanel from '../components/AlertasVeiculosPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_FORM = {
  nome: '',
  descricao: '',
  categoria: 'maquinas_e_veiculos',
  subtipo: 'maquina',
  numero_serie_placa: '',
  valor_aquisicao: '',
  data_aquisicao: '',
  status: 'ativo',
  responsavel_id: '',
  observacoes: '',
  location_id: '',
  centro_de_custo_id: '',
  // Comum
  marca: '',
  modelo: '',
  ano_fabricacao: '',
  horimetro_km_inicial: '',
  machine_id: '',
  // Veículos da frota
  placa: '',
  chassis: '',
  renavam: '',
  data_vencimento_licenciamento: '',
  intervalo_revisao_km: '',
  km_ultima_revisao: '',
  cnh_categoria_exigida: '',
  // Imóveis
  endereco_completo: '',
  area_m2: '',
  matricula_imovel: '',
  tipo_imovel: '',
  // Outros
  localizacao_fisica: '',
};

const SUBTIPOS_VEICULO = new Set([
  'caminhonete', 'caminhao', 'caminhao_munck', 'carreta',
  'onibus', 'micro_onibus', 'veiculo_utilitario', 'veiculo_passeio',
]);

// Campos relevantes por categoria/subtipo
const CATEGORY_FIELDS = {
  maquinas_e_veiculos: {
    fields: ['machine_id', 'marca', 'modelo', 'ano_fabricacao', 'horimetro_km_inicial'],
    label: 'Máquinas e Veículos',
    hint: 'Subtipo `Máquina` usa horímetro/serial. Subtipos de veículo expõem placa, chassis, RENAVAM, licenciamento e revisão.',
  },
  imovel: { fields: ['tipo_imovel', 'endereco_completo', 'area_m2', 'matricula_imovel'], label: 'Imóvel', hint: 'Endereço, área e matrícula registral' },
  equipamento: { fields: ['marca', 'modelo', 'localizacao_fisica'], label: 'Equipamento', hint: 'Marca, modelo e localização física dentro da operação' },
  ferramenta: { fields: ['marca', 'modelo', 'localizacao_fisica'], label: 'Ferramenta', hint: 'Marca, modelo e localização física dentro da operação' },
};

const SUBTIPO_LABELS = {
  maquina: 'Máquina',
  caminhonete: 'Caminhonete',
  caminhao: 'Caminhão',
  caminhao_munck: 'Caminhão Munck',
  carreta: 'Carreta',
  onibus: 'Ônibus',
  micro_onibus: 'Micro Ônibus',
  veiculo_utilitario: 'Veículo Utilitário',
  veiculo_passeio: 'Veículo de Passeio',
};

const STATUS_COLOR = {
  ativo: 'bg-green-500/20 text-green-300 border-green-500/40',
  inativo: 'bg-gray-500/20 text-gray-300 border-gray-500/40',
  vendido: 'bg-blue-500/20 text-blue-300 border-blue-500/40',
};

const formatBRL = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return '—';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('pt-BR'); } catch { return iso; }
};

export default function Inventario() {
  const [ativos, setAtivos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [tiposImovel, setTiposImovel] = useState([]);
  const [machinesOptions, setMachinesOptions] = useState([]);
  const [colaboradores, setColaboradores] = useState([]);
  const [locationsOptions, setLocationsOptions] = useState([]);
  const [centrosCustoOptions, setCentrosCustoOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategoria, setFilterCategoria] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDataIni, setFilterDataIni] = useState('');
  const [filterDataFim, setFilterDataFim] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [machineSearch, setMachineSearch] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [sellModal, setSellModal] = useState(null); // ativo a vender
  const [sellForm, setSellForm] = useState({ valor_venda: '', data_venda: new Date().toISOString().slice(0, 10), observacao: '' });
  const [selling, setSelling] = useState(false);

  const fetchCatalogs = async () => {
    try {
      const [c, s, t, m, col, locs, cc] = await Promise.all([
        fetch(`${API_URL}/api/inventario/categorias`, { headers: authHeaders(), cache: 'no-store' }),
        fetch(`${API_URL}/api/inventario/status`, { headers: authHeaders(), cache: 'no-store' }),
        fetch(`${API_URL}/api/inventario/tipos-imovel`, { headers: authHeaders(), cache: 'no-store' }),
        fetch(`${API_URL}/api/inventario/machines-options`, { headers: authHeaders(), cache: 'no-store' }),
        fetch(`${API_URL}/api/colaboradores/options`, { headers: authHeaders(), cache: 'no-store' }),
        fetch(`${API_URL}/api/inventario/locations-options`, { headers: authHeaders(), cache: 'no-store' }),
        fetch(`${API_URL}/api/centros-custo`, { headers: authHeaders(), cache: 'no-store' }),
      ]);
      if (c.ok) setCategorias(await c.json());
      if (s.ok) setStatusOptions(await s.json());
      if (t.ok) setTiposImovel(await t.json());
      if (m.ok) setMachinesOptions(await m.json());
      if (col.ok) setColaboradores(await col.json());
      if (locs.ok) setLocationsOptions(await locs.json());
      if (cc.ok) setCentrosCustoOptions(await cc.json());
    } catch (e) {
      console.error(e);
    }
  };

  const fetchAtivos = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterCategoria !== 'all') params.append('categoria', filterCategoria);
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterDataIni) params.append('data_inicio', filterDataIni);
      if (filterDataFim) params.append('data_fim', filterDataFim);
      if (search) params.append('search', search);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const r = await fetch(`${API_URL}/api/inventario/ativos${qs}`, { headers: authHeaders(), cache: 'no-store' });
      if (r.ok) setAtivos(await r.json());
    } catch (e) {
      toast.error('Falha ao carregar inventário');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCatalogs(); }, []);
  useEffect(() => { fetchAtivos(); }, [filterCategoria, filterStatus, filterDataIni, filterDataFim]);

  const onSearchKey = (e) => { if (e.key === 'Enter') fetchAtivos(); };

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (a) => {
    setEditing(a);
    setForm({
      nome: a.nome || '',
      descricao: a.descricao || '',
      categoria: (a.categoria === 'maquina' || a.categoria === 'veiculo') ? 'maquinas_e_veiculos' : (a.categoria || 'maquinas_e_veiculos'),
      subtipo: a.subtipo || (a.categoria === 'maquina' ? 'maquina' : (a.categoria === 'veiculo' ? 'veiculo_passeio' : '')),
      numero_serie_placa: a.numero_serie_placa || '',
      valor_aquisicao: a.valor_aquisicao ?? '',
      data_aquisicao: a.data_aquisicao || '',
      status: a.status || 'ativo',
      responsavel_id: a.responsavel_id || '',
      observacoes: a.observacoes || '',
      location_id: a.location_id || '',
      centro_de_custo_id: a.centro_de_custo_id || '',
      marca: a.marca || '',
      modelo: a.modelo || '',
      ano_fabricacao: a.ano_fabricacao ?? '',
      horimetro_km_inicial: a.horimetro_km_inicial ?? '',
      machine_id: a.machine_id || '',
      placa: a.placa || '',
      chassis: a.chassis || '',
      renavam: a.renavam || '',
      data_vencimento_licenciamento: a.data_vencimento_licenciamento || '',
      intervalo_revisao_km: a.intervalo_revisao_km ?? '',
      km_ultima_revisao: a.km_ultima_revisao ?? '',
      cnh_categoria_exigida: a.cnh_categoria_exigida || '',
      endereco_completo: a.endereco_completo || '',
      area_m2: a.area_m2 ?? '',
      matricula_imovel: a.matricula_imovel || '',
      tipo_imovel: a.tipo_imovel || '',
      localizacao_fisica: a.localizacao_fisica || '',
    });
    setMachineSearch('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditing(null);
    setForm(EMPTY_FORM);
    setMachineSearch('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!form.nome.trim() || form.nome.trim().length < 2) {
      toast.error('Nome obrigatório (mín. 2 caracteres)');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        nome: form.nome.trim(),
        descricao: form.descricao || null,
        categoria: form.categoria,
        subtipo: form.categoria === 'maquinas_e_veiculos' ? (form.subtipo || null) : null,
        numero_serie_placa: form.numero_serie_placa || null,
        valor_aquisicao: form.valor_aquisicao !== '' ? Number(form.valor_aquisicao) : null,
        data_aquisicao: form.data_aquisicao || null,
        status: form.status,
        responsavel_id: form.responsavel_id || null,
        observacoes: form.observacoes || null,
        location_id: form.location_id || null,
        centro_de_custo_id: form.centro_de_custo_id || null,
        marca: form.marca || null,
        modelo: form.modelo || null,
        ano_fabricacao: form.ano_fabricacao !== '' ? Number(form.ano_fabricacao) : null,
        horimetro_km_inicial: form.horimetro_km_inicial !== '' ? Number(form.horimetro_km_inicial) : null,
        machine_id: form.machine_id || null,
        placa: form.placa ? form.placa.trim().toUpperCase() : null,
        chassis: form.chassis ? form.chassis.trim().toUpperCase() : null,
        renavam: form.renavam || null,
        data_vencimento_licenciamento: form.data_vencimento_licenciamento || null,
        intervalo_revisao_km: form.intervalo_revisao_km !== '' ? Number(form.intervalo_revisao_km) : null,
        km_ultima_revisao: form.km_ultima_revisao !== '' ? Number(form.km_ultima_revisao) : null,
        cnh_categoria_exigida: form.cnh_categoria_exigida || null,
        endereco_completo: form.endereco_completo || null,
        area_m2: form.area_m2 !== '' ? Number(form.area_m2) : null,
        matricula_imovel: form.matricula_imovel || null,
        tipo_imovel: form.tipo_imovel || null,
        localizacao_fisica: form.localizacao_fisica || null,
      };
      const url = editing
        ? `${API_URL}/api/inventario/ativos/${editing.id}`
        : `${API_URL}/api/inventario/ativos`;
      const method = editing ? 'PUT' : 'POST';
      const r = await fetch(url, {
        method,
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Falha ao salvar');
      }
      toast.success(editing ? 'Ativo atualizado' : 'Ativo cadastrado');
      closeModal();
      fetchAtivos();
    } catch (err) {
      toast.error(err.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (a) => {
    try {
      const r = await fetch(`${API_URL}/api/inventario/ativos/${a.id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Falha ao excluir');
      }
      toast.success('Ativo excluído');
      setConfirmDelete(null);
      fetchAtivos();
    } catch (e) {
      toast.error(e.message);
    }
  };

  // Auto-preenche dados do ativo quando uma máquina é vinculada (apenas em criação)
  const handleMachineSelect = (machineId) => {
    setForm((prev) => ({ ...prev, machine_id: machineId }));
    if (!machineId || editing) return;
    const m = machinesOptions.find((x) => x.id === machineId);
    if (!m) return;
    setForm((prev) => ({
      ...prev,
      machine_id: machineId,
      marca: prev.marca || m.brand || '',
      modelo: prev.modelo || m.model || '',
      numero_serie_placa: prev.numero_serie_placa || m.serial_number || '',
      nome: prev.nome || `${m.brand || ''} ${m.model || ''} (${m.serial_number || ''})`.trim(),
    }));
    toast.success('Dados da máquina vinculada preenchidos');
  };

  // Sugere centro de custo automático quando o Local de Operação é selecionado
  const handleLocationSelect = async (locationId) => {
    setForm((prev) => ({ ...prev, location_id: locationId, centro_de_custo_id: '' }));
    if (!locationId) return;
    try {
      const r = await fetch(`${API_URL}/api/centros-custo/sugerir-por-local/${locationId}`, {
        headers: authHeaders(), cache: 'no-store',
      });
      if (r.ok) {
        const data = await r.json();
        if (data.centro_de_custo_id) {
          setForm((prev) => ({ ...prev, centro_de_custo_id: data.centro_de_custo_id }));
          toast.info(`Centro de Custo sugerido: ${data.nome}`);
        }
      }
    } catch {}
  };

  const openSellModal = (a) => {
    setSellModal(a);
    setSellForm({
      valor_venda: a.valor_aquisicao ? String(a.valor_aquisicao) : '',
      data_venda: new Date().toISOString().slice(0, 10),
      observacao: '',
    });
  };

  const closeSellModal = () => {
    setSellModal(null);
    setSellForm({ valor_venda: '', data_venda: new Date().toISOString().slice(0, 10), observacao: '' });
  };

  const handleSell = async (e) => {
    e.preventDefault();
    if (selling) return;
    const valor = Number(sellForm.valor_venda);
    if (!valor || valor <= 0) {
      toast.error('Informe um valor de venda válido');
      return;
    }
    if (!sellForm.data_venda) {
      toast.error('Data da venda obrigatória');
      return;
    }
    setSelling(true);
    try {
      const r = await fetch(`${API_URL}/api/inventario/ativos/${sellModal.id}/vender`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          valor_venda: valor,
          data_venda: sellForm.data_venda,
          observacao: sellForm.observacao || null,
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Falha ao registrar venda');
      }
      const data = await r.json();
      toast.success(`Venda registrada — lançamento financeiro #${data.lancamento_id?.slice(-6)} criado`);
      closeSellModal();
      fetchAtivos();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSelling(false);
    }
  };

  const stats = useMemo(() => {
    const total = ativos.length;
    const ativosOnly = ativos.filter((a) => a.status === 'ativo').length;
    const totalValor = ativos.reduce((sum, a) => sum + (Number(a.valor_aquisicao) || 0), 0);
    return { total, ativosOnly, totalValor };
  }, [ativos]);

  return (
    <div className="space-y-6 p-4 sm:p-6" data-testid="inventario-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-2">
            <Package size={28} className="text-yellow-400" /> Inventário de Ativos
          </h1>
          <p className="text-sm text-gray-400 mt-1">Cadastro e controle de máquinas e veículos, imóveis, equipamentos e ferramentas</p>
        </div>
        <button
          onClick={openCreate}
          data-testid="new-ativo-button"
          className="flex items-center justify-center gap-2 px-4 py-2.5 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold rounded-lg transition-colors"
        >
          <Plus size={18} /> Novo Ativo
        </button>
      </div>

      {/* Painel de Alertas de Veículos (separado dos alertas de máquinas) */}
      <AlertasVeiculosPanel refreshKey={ativos.length} />

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-[#111] border border-gray-800 rounded-xl p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wide">Total cadastrados</div>
          <div className="text-2xl font-bold text-white mt-1" data-testid="kpi-total">{stats.total}</div>
        </div>
        <div className="bg-[#111] border border-gray-800 rounded-xl p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wide">Ativos em uso</div>
          <div className="text-2xl font-bold text-green-400 mt-1" data-testid="kpi-ativos">{stats.ativosOnly}</div>
        </div>
        <div className="bg-[#111] border border-gray-800 rounded-xl p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wide">Valor total de aquisição</div>
          <div className="text-2xl font-bold text-yellow-400 mt-1" data-testid="kpi-valor">{formatBRL(stats.totalValor)}</div>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-[#111] border border-gray-800 rounded-xl p-4 space-y-3">
        <div className="flex items-center gap-2 text-sm text-gray-400">
          <Filter size={16} /> Filtros
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={onSearchKey}
              placeholder="Buscar nome / nº série / placa"
              data-testid="search-input"
              className="w-full pl-9 pr-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:border-yellow-500 focus:outline-none transition-colors text-sm"
            />
          </div>
          <select
            value={filterCategoria}
            onChange={(e) => setFilterCategoria(e.target.value)}
            data-testid="filter-categoria"
            className="px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none transition-colors text-sm"
          >
            <option value="all">Todas categorias</option>
            {categorias.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            data-testid="filter-status"
            className="px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none transition-colors text-sm"
          >
            <option value="all">Todos status</option>
            {statusOptions.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <input
            type="date"
            value={filterDataIni}
            onChange={(e) => setFilterDataIni(e.target.value)}
            data-testid="filter-data-ini"
            title="Aquisição a partir de"
            className="px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none transition-colors text-sm"
          />
          <input
            type="date"
            value={filterDataFim}
            onChange={(e) => setFilterDataFim(e.target.value)}
            data-testid="filter-data-fim"
            title="Aquisição até"
            className="px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none transition-colors text-sm"
          />
        </div>
      </div>

      {/* Lista */}
      {loading ? (
        <div className="flex items-center justify-center py-12 text-gray-400">
          <Loader size={20} className="animate-spin mr-2" /> Carregando...
        </div>
      ) : ativos.length === 0 ? (
        <div className="text-center py-16 text-gray-500 bg-[#111] border border-gray-800 rounded-xl">
          <Package size={36} className="mx-auto mb-2 text-gray-600" />
          <p>Nenhum ativo encontrado</p>
          <p className="text-xs mt-1">Cadastre o primeiro clicando em "Novo Ativo"</p>
        </div>
      ) : (
        <div className="bg-[#111] border border-gray-800 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#0d0d0d] border-b border-gray-800">
                <tr className="text-left text-xs text-gray-500 uppercase">
                  <th className="px-4 py-3">Ativo</th>
                  <th className="px-4 py-3 hidden md:table-cell">Categoria</th>
                  <th className="px-4 py-3 hidden lg:table-cell">Nº Série / Placa</th>
                  <th className="px-4 py-3 hidden lg:table-cell">Aquisição</th>
                  <th className="px-4 py-3">Valor</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {ativos.map((a) => (
                  <tr key={a.id} className="hover:bg-[#0d0d0d] transition-colors" data-testid={`row-ativo-${a.id}`}>
                    <td className="px-4 py-3">
                      <div className="text-white font-medium">{a.nome}</div>
                      {a.descricao && <div className="text-xs text-gray-500 truncate max-w-xs">{a.descricao}</div>}
                      <div className="md:hidden text-xs text-gray-400 mt-1">{a.categoria_label}</div>
                      {a.location_info && (
                        <div className="text-[11px] text-gray-500 mt-0.5 flex items-center gap-1">
                          <MapPin size={10} /> {a.location_info.full_name}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-gray-300">{a.categoria_label}</td>
                    <td className="px-4 py-3 hidden lg:table-cell text-gray-400 font-mono text-xs">{a.numero_serie_placa || '—'}</td>
                    <td className="px-4 py-3 hidden lg:table-cell text-gray-400">{formatDate(a.data_aquisicao)}</td>
                    <td className="px-4 py-3 text-gray-200">{formatBRL(a.valor_aquisicao)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${STATUS_COLOR[a.status] || 'bg-gray-500/20 text-gray-300 border-gray-500/40'}`}>
                        {a.status_label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {a.status === 'ativo' && (
                          <button
                            onClick={() => openSellModal(a)}
                            data-testid={`sell-${a.id}`}
                            className="p-2 text-gray-400 hover:text-green-400 hover:bg-gray-800 rounded transition-colors"
                            title="Registrar venda"
                          >
                            <ShoppingCart size={15} />
                          </button>
                        )}
                        <button
                          onClick={() => openEdit(a)}
                          data-testid={`edit-${a.id}`}
                          className="p-2 text-gray-400 hover:text-yellow-400 hover:bg-gray-800 rounded transition-colors"
                          title="Editar"
                        >
                          <Edit2 size={15} />
                        </button>
                        <button
                          onClick={() => setConfirmDelete(a)}
                          data-testid={`delete-${a.id}`}
                          className="p-2 text-gray-400 hover:text-red-400 hover:bg-gray-800 rounded transition-colors"
                          title="Excluir"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal de cadastro/edição */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4">
          <div className="bg-[#111] border border-gray-800 rounded-xl w-full max-w-2xl max-h-[90vh] flex flex-col" data-testid="ativo-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-800">
              <h3 className="text-lg font-semibold text-white">
                {editing ? 'Editar Ativo' : 'Novo Ativo'}
              </h3>
              <button onClick={closeModal} className="text-gray-400 hover:text-white" data-testid="close-modal">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Nome *</label>
                <input
                  required
                  value={form.nome}
                  onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  data-testid="form-nome"
                  className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none text-sm"
                  placeholder="Ex: Trator de Esteiras Fiatallis FD170 - Unidade 11"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Categoria *</label>
                  <select
                    value={form.categoria}
                    onChange={(e) => {
                      const cat = e.target.value;
                      setForm((p) => ({
                        ...p,
                        categoria: cat,
                        subtipo: cat === 'maquinas_e_veiculos' ? (p.subtipo || 'maquina') : '',
                      }));
                    }}
                    data-testid="form-categoria"
                    className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none text-sm"
                  >
                    {categorias.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Status</label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    data-testid="form-status"
                    className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none text-sm"
                  >
                    {statusOptions.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
              </div>
              {form.categoria === 'maquinas_e_veiculos' && (
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Subtipo *</label>
                  <select
                    required
                    value={form.subtipo}
                    onChange={(e) => setForm({ ...form, subtipo: e.target.value })}
                    data-testid="form-subtipo"
                    className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none text-sm"
                  >
                    {Object.entries(SUBTIPO_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                  <p className="text-[11px] text-gray-500 mt-1">
                    Subtipos de veículo (caminhonete, caminhão, utilitário, passeio) exibem campos de placa, chassis, RENAVAM, licenciamento e revisão por km.
                  </p>
                </div>
              )}
              <div>
                <label className="block text-xs text-gray-400 mb-1 flex items-center gap-1"><Hash size={12} /> Nº de série / Placa</label>
                <input
                  value={form.numero_serie_placa}
                  onChange={(e) => setForm({ ...form, numero_serie_placa: e.target.value })}
                  data-testid="form-numero-serie"
                  className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none text-sm font-mono"
                  placeholder="TE-11FD170 / ABC1234 / SN12345"
                />
              </div>

              {/* === Campos específicos por categoria === */}
              {CATEGORY_FIELDS[form.categoria] && (
                <div
                  className="rounded-lg border border-yellow-500/30 bg-yellow-500/5 p-3 space-y-3"
                  data-testid="category-specific-block"
                >
                  <div className="text-xs text-yellow-300 font-semibold uppercase tracking-wide flex items-center gap-1">
                    Campos de {CATEGORY_FIELDS[form.categoria].label}
                  </div>
                  <p className="text-[11px] text-gray-400">{CATEGORY_FIELDS[form.categoria].hint}</p>

                  {/* Maquina / Veiculo: vínculo com cadastro de máquinas */}
                  {CATEGORY_FIELDS[form.categoria].fields.includes('machine_id') && (
                    <div>
                      <label className="block text-xs text-gray-300 mb-1">Vínculo com máquina cadastrada (opcional)</label>
                      <input
                        type="text"
                        value={machineSearch}
                        onChange={(e) => setMachineSearch(e.target.value)}
                        placeholder="Buscar por marca, modelo ou nº de série…"
                        data-testid="machine-search"
                        className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none text-sm mb-2"
                      />
                      <select
                        value={form.machine_id}
                        onChange={(e) => handleMachineSelect(e.target.value)}
                        data-testid="form-machine-id"
                        className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none text-sm"
                      >
                        <option value="">— Não vincular —</option>
                        {machinesOptions
                          .filter((m) => !machineSearch || m.nome.toLowerCase().includes(machineSearch.toLowerCase()))
                          .map((m) => (
                            <option key={m.id} value={m.id}>{m.nome}</option>
                          ))}
                      </select>
                      {form.machine_id && (
                        <p className="text-[11px] text-gray-500 mt-1">
                          Centro de custo da Etapa 2 herdará dados operacionais dessa máquina.
                        </p>
                      )}
                    </div>
                  )}

                  {/* Marca + Modelo (máquina, veículo, equipamento, ferramenta) */}
                  {CATEGORY_FIELDS[form.categoria].fields.includes('marca') && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-300 mb-1">Marca</label>
                        <input
                          value={form.marca}
                          onChange={(e) => setForm({ ...form, marca: e.target.value })}
                          data-testid="form-marca"
                          className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none text-sm"
                          placeholder="Ex: Fiatallis"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-300 mb-1">Modelo</label>
                        <input
                          value={form.modelo}
                          onChange={(e) => setForm({ ...form, modelo: e.target.value })}
                          data-testid="form-modelo"
                          className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none text-sm"
                          placeholder="Ex: FD170"
                        />
                      </div>
                    </div>
                  )}

                  {/* Ano + Horímetro/KM (máquina, veículo) */}
                  {CATEGORY_FIELDS[form.categoria].fields.includes('ano_fabricacao') && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-300 mb-1">Ano de fabricação</label>
                        <input
                          type="number"
                          min="1900"
                          max="2100"
                          value={form.ano_fabricacao}
                          onChange={(e) => setForm({ ...form, ano_fabricacao: e.target.value })}
                          data-testid="form-ano"
                          className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none text-sm"
                          placeholder="2024"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-300 mb-1">
                          Horímetro / Quilometragem inicial
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={form.horimetro_km_inicial}
                          onChange={(e) => setForm({ ...form, horimetro_km_inicial: e.target.value })}
                          data-testid="form-horimetro"
                          className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none text-sm"
                          placeholder="Em horas (máquina) ou km (veículo)"
                        />
                      </div>
                    </div>
                  )}

                  {/* Veículos da frota: placa, chassis, RENAVAM, licenciamento, revisão */}
                  {form.categoria === 'maquinas_e_veiculos' && SUBTIPOS_VEICULO.has(form.subtipo) && (
                    <div className="rounded-lg border border-blue-500/30 bg-blue-500/5 p-3 space-y-3" data-testid="veiculo-fields-block">
                      <div className="text-xs text-blue-300 font-semibold uppercase tracking-wide">
                        Dados do Veículo
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-300 mb-1">Placa</label>
                          <input
                            value={form.placa}
                            onChange={(e) => setForm({ ...form, placa: e.target.value.toUpperCase() })}
                            data-testid="form-placa"
                            className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-blue-500 focus:outline-none text-sm font-mono uppercase"
                            placeholder="ABC1D23"
                            maxLength={8}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-300 mb-1">RENAVAM</label>
                          <input
                            value={form.renavam}
                            onChange={(e) => setForm({ ...form, renavam: e.target.value.replace(/\D/g, '') })}
                            data-testid="form-renavam"
                            className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-blue-500 focus:outline-none text-sm font-mono"
                            placeholder="12345678901"
                            maxLength={11}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-300 mb-1">Chassis</label>
                        <input
                          value={form.chassis}
                          onChange={(e) => setForm({ ...form, chassis: e.target.value.toUpperCase() })}
                          data-testid="form-chassis"
                          className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-blue-500 focus:outline-none text-sm font-mono uppercase"
                          placeholder="9BD11111111111111"
                          maxLength={17}
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-300 mb-1 flex items-center gap-1">
                            <Calendar size={12} /> Vencimento do licenciamento
                          </label>
                          <input
                            type="date"
                            value={form.data_vencimento_licenciamento}
                            onChange={(e) => setForm({ ...form, data_vencimento_licenciamento: e.target.value })}
                            data-testid="form-licenciamento"
                            className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-blue-500 focus:outline-none text-sm"
                          />
                          <p className="text-[11px] text-gray-500 mt-1">Alerta exibido 30 dias antes</p>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-300 mb-1">Intervalo de revisão</label>
                          <select
                            value={form.intervalo_revisao_km}
                            onChange={(e) => setForm({ ...form, intervalo_revisao_km: e.target.value })}
                            data-testid="form-intervalo-revisao"
                            className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-blue-500 focus:outline-none text-sm"
                          >
                            <option value="">— Não monitorar —</option>
                            <option value="5000">5.000 km</option>
                            <option value="10000">10.000 km</option>
                          </select>
                        </div>
                      </div>
                      {form.intervalo_revisao_km && (
                        <div>
                          <label className="block text-xs text-gray-300 mb-1">Km da última revisão</label>
                          <input
                            type="number"
                            min="0"
                            value={form.km_ultima_revisao}
                            onChange={(e) => setForm({ ...form, km_ultima_revisao: e.target.value })}
                            data-testid="form-km-ultima-revisao"
                            className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-blue-500 focus:outline-none text-sm"
                            placeholder="Ex: 50000"
                          />
                          <p className="text-[11px] text-gray-500 mt-1">Base para calcular a próxima revisão. Atualizado automaticamente ao registrar uma revisão no hodômetro.</p>
                        </div>
                      )}
                      <div>
                        <label className="block text-xs text-gray-300 mb-1">Categoria CNH exigida *</label>
                        <select
                          value={form.cnh_categoria_exigida || ''}
                          onChange={(e) => setForm({ ...form, cnh_categoria_exigida: e.target.value })}
                          data-testid="form-cnh-categoria"
                          required
                          className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-blue-500 focus:outline-none text-sm"
                        >
                          <option value="">— Selecione —</option>
                          <option value="A">A (motocicleta)</option>
                          <option value="B">B (carro/caminhonete leve)</option>
                          <option value="C">C (caminhão)</option>
                          <option value="D">D (ônibus/micro-ônibus)</option>
                          <option value="E">E (carreta/Munck)</option>
                        </select>
                        <p className="text-[11px] text-gray-500 mt-1">
                          Motoristas alocados precisam ter exatamente esta categoria de CNH e não vencida.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Imóvel: tipo + endereço + área + matrícula */}
                  {CATEGORY_FIELDS[form.categoria].fields.includes('tipo_imovel') && (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-300 mb-1">Tipo de imóvel</label>
                          <select
                            value={form.tipo_imovel}
                            onChange={(e) => setForm({ ...form, tipo_imovel: e.target.value })}
                            data-testid="form-tipo-imovel"
                            className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none text-sm"
                          >
                            <option value="">— Selecione —</option>
                            {tiposImovel.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-300 mb-1">Área (m²)</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={form.area_m2}
                            onChange={(e) => setForm({ ...form, area_m2: e.target.value })}
                            data-testid="form-area"
                            className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none text-sm"
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-300 mb-1">Endereço completo</label>
                        <input
                          value={form.endereco_completo}
                          onChange={(e) => setForm({ ...form, endereco_completo: e.target.value })}
                          data-testid="form-endereco"
                          className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none text-sm"
                          placeholder="Rua, número, bairro, cidade — UF, CEP"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-300 mb-1">Matrícula do imóvel</label>
                        <input
                          value={form.matricula_imovel}
                          onChange={(e) => setForm({ ...form, matricula_imovel: e.target.value })}
                          data-testid="form-matricula"
                          className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none text-sm font-mono"
                          placeholder="Ex: 12.345 - Cartório 2º Of."
                        />
                      </div>
                    </>
                  )}

                  {/* Equipamento / Ferramenta: localização física */}
                  {CATEGORY_FIELDS[form.categoria].fields.includes('localizacao_fisica') && (
                    <div>
                      <label className="block text-xs text-gray-300 mb-1">Localização física dentro da operação</label>
                      <input
                        value={form.localizacao_fisica}
                        onChange={(e) => setForm({ ...form, localizacao_fisica: e.target.value })}
                        data-testid="form-localizacao"
                        className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none text-sm"
                        placeholder="Ex: Almoxarifado 02 - Prateleira A3"
                      />
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1 flex items-center gap-1"><DollarSign size={12} /> Valor de aquisição (R$)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.valor_aquisicao}
                    onChange={(e) => setForm({ ...form, valor_aquisicao: e.target.value })}
                    data-testid="form-valor"
                    className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none text-sm"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1 flex items-center gap-1"><Calendar size={12} /> Data de aquisição</label>
                  <input
                    type="date"
                    value={form.data_aquisicao}
                    onChange={(e) => setForm({ ...form, data_aquisicao: e.target.value })}
                    data-testid="form-data"
                    className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Responsável (opcional)</label>
                <select
                  value={form.responsavel_id}
                  onChange={(e) => setForm({ ...form, responsavel_id: e.target.value })}
                  data-testid="form-responsavel"
                  className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none text-sm"
                >
                  <option value="">— Nenhum —</option>
                  {colaboradores.map((c) => (
                    <option key={c.id} value={c.id}>{c.nome}{c.cargo ? ` (${c.cargo})` : ''}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1 flex items-center gap-1"><MapPin size={12} /> Local de Operação</label>
                <select
                  value={form.location_id}
                  onChange={(e) => handleLocationSelect(e.target.value)}
                  data-testid="form-location-id"
                  className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none text-sm"
                >
                  <option value="">— Nenhum (sem alocação) —</option>
                  {locationsOptions.map((l) => (
                    <option key={l.id} value={l.id}>{l.nome}</option>
                  ))}
                </select>
                <p className="text-[11px] text-gray-500 mt-1">
                  Dimensão geográfica de custo. Ao selecionar, sugerimos automaticamente um Centro de Custo ativo.
                </p>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1 flex items-center gap-1"><Tag size={12} /> Centro de Custo (opcional)</label>
                <select
                  value={form.centro_de_custo_id}
                  onChange={(e) => setForm({ ...form, centro_de_custo_id: e.target.value })}
                  data-testid="form-centro-custo-id"
                  className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none text-sm"
                >
                  <option value="">— Nenhum —</option>
                  {centrosCustoOptions
                    .filter((cc) => !form.location_id || cc.location_id === form.location_id || cc.id === form.centro_de_custo_id)
                    .map((cc) => (
                      <option key={cc.id} value={cc.id}>{cc.nome}{cc.status !== 'ativo' ? ' (inativo)' : ''}</option>
                    ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Descrição</label>
                <textarea
                  rows={2}
                  value={form.descricao}
                  onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                  data-testid="form-descricao"
                  className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none text-sm resize-none"
                  placeholder="Detalhes técnicos, modelo, marca..."
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Observações</label>
                <textarea
                  rows={2}
                  value={form.observacoes}
                  onChange={(e) => setForm({ ...form, observacoes: e.target.value })}
                  data-testid="form-observacoes"
                  className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none text-sm resize-none"
                  placeholder="Notas adicionais..."
                />
              </div>
            </form>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-800 bg-[#0d0d0d]">
              <button
                type="button"
                onClick={closeModal}
                className="px-4 py-2 text-sm text-gray-300 hover:text-white bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
                data-testid="cancel-button"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={saving}
                data-testid="save-button"
                className="px-4 py-2 text-sm bg-yellow-500 hover:bg-yellow-400 disabled:opacity-50 text-black font-semibold rounded-lg transition-colors"
              >
                {saving ? 'Salvando...' : (editing ? 'Salvar' : 'Cadastrar')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmação de exclusão */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#111] border border-gray-800 rounded-xl w-full max-w-md p-5">
            <div className="flex items-start gap-3 mb-4">
              <AlertCircle size={24} className="text-red-400 shrink-0 mt-0.5" />
              <div>
                <h3 className="text-lg font-semibold text-white">Excluir ativo?</h3>
                <p className="text-sm text-gray-400 mt-1">
                  "<span className="text-white">{confirmDelete.nome}</span>" será marcado como excluído. O histórico é preservado.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 text-sm text-gray-300 hover:text-white bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
                data-testid="cancel-delete"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleDelete(confirmDelete)}
                className="px-4 py-2 text-sm bg-red-500 hover:bg-red-400 text-white font-semibold rounded-lg transition-colors"
                data-testid="confirm-delete"
              >
                Confirmar exclusão
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Modal de Venda de Ativo */}
      {sellModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4">
          <div className="bg-[#111] border border-gray-800 rounded-xl w-full max-w-md" data-testid="sell-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-800">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <ShoppingCart size={20} className="text-green-400" /> Vender Ativo
              </h3>
              <button onClick={closeSellModal} className="text-gray-400 hover:text-white" data-testid="close-sell-modal">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSell} className="px-5 py-4 space-y-3">
              <div className="bg-[#0A0A0A] border border-gray-800 rounded-lg p-3">
                <p className="text-xs text-gray-500 uppercase">Ativo</p>
                <p className="text-white font-medium">{sellModal.nome}</p>
                {sellModal.valor_aquisicao && (
                  <p className="text-xs text-gray-400 mt-1">
                    Aquisição: {formatBRL(sellModal.valor_aquisicao)}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1 flex items-center gap-1">
                  <DollarSign size={12} /> Valor da venda (R$) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  required
                  value={sellForm.valor_venda}
                  onChange={(e) => setSellForm({ ...sellForm, valor_venda: e.target.value })}
                  data-testid="sell-valor"
                  className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-green-500 focus:outline-none text-sm"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1 flex items-center gap-1">
                  <Calendar size={12} /> Data da venda *
                </label>
                <input
                  type="date"
                  required
                  value={sellForm.data_venda}
                  onChange={(e) => setSellForm({ ...sellForm, data_venda: e.target.value })}
                  data-testid="sell-data"
                  className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-green-500 focus:outline-none text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Observação</label>
                <textarea
                  rows={2}
                  value={sellForm.observacao}
                  onChange={(e) => setSellForm({ ...sellForm, observacao: e.target.value })}
                  data-testid="sell-obs"
                  className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-green-500 focus:outline-none text-sm resize-none"
                  placeholder="Comprador, nota fiscal, observação..."
                />
              </div>
              <div className="text-[11px] text-gray-500 bg-yellow-500/5 border border-yellow-500/20 rounded p-2">
                Esta venda gerará um lançamento de <strong className="text-yellow-300">entrada</strong> no Financeiro com a categoria "Venda de Ativo" e marcará este ativo como <strong className="text-blue-300">vendido</strong>.
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t border-gray-800">
                <button
                  type="button"
                  onClick={closeSellModal}
                  className="px-4 py-2 text-sm text-gray-300 hover:text-white bg-gray-800 hover:bg-gray-700 rounded-lg"
                  data-testid="cancel-sell"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={selling}
                  data-testid="confirm-sell"
                  className="px-4 py-2 text-sm bg-green-500 hover:bg-green-400 disabled:opacity-50 text-black font-semibold rounded-lg"
                >
                  {selling ? 'Registrando...' : 'Confirmar venda'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
